/********************************** WHITEBOPHIR ***************************************************
 * This class was taken and adapted for the EDUAC project from the WHITEBOPHIR project            *
 * https://github.com/lovasoa/whitebophir, keeping the same distribution and license conditions.  *
 **************************************************************************************************
 */
import Vue from 'vue';
import Component from 'vue-class-component';
import {Emit, Mixins} from 'vue-property-decorator';

import {BOARD_CONFIG_PARAMETERS, BOARD_EVENTS, toolRanges} from '@/values/ed-whiteboard.values';
import {RESIZE_TYPES, WBO_CURSOR_TYPES} from '@/core/values/ed-transparency-meeting.values';
import BoardAnnotations from '@/homework/models/board-annotations.model';
import EdWboDrawer from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/ed-wbo-drawer';
import EdWboDrawerZoom from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/zoom/ed-wbo-drawer-zoom';
import {_MEETINGS_STORE} from '@/meetings/values/meetings.values';
import {HOMEWORKS_EVENTS} from '@/homework/values/homeworks.values';
import {
    CursorStyle,
    OthersCursorMode
} from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-config.enum';
import WboClient from '@/components/common/ed-board/ed-board-tools/wbo-client/wbo-client';
import WboDefaultDrawingAdapter
    from '@/components/common/ed-board/ed-board-tools/wbo-client/drawing/wbo-default-drawing-adapter';
import WboSocketIoTransportProvider
    from '@/components/common/ed-board/ed-board-tools/wbo-client/transport/wbo-socketio-transport-provider';
import WboDocument from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-document';
import WboPendingChangesQueue
    from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-pending-changes-queue';
import {BoardMixin} from '@/mixins/common/board.mixin';
import BrowserService from '@/providers/browser-utils/browser.service';
import {
    WboVersionedAnnotationData
} from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-annotation.model';
import {AuthProvider} from '@/auth/providers/auth.provider';
import { WboPersistenceLocatorProvider } from './ed-board-tools/wbo-client/persistence/wbo-persistence-locator-provider';
import { NETWORK_SETTINGS } from '@/core/values/app.values';
import WboMixedTransportProvider from './ed-board-tools/wbo-client/transport/wbo-mixed-transport-provider';



declare const window: any;

// Simple, fast, synchronous hash function (djb2)
function simpleHash(str: string): string {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) + str.charCodeAt(i); // hash * 33 + c
    }
    return (hash >>> 0).toString(16);
}

@Component({})
export class EdBoardV2 extends Mixins(BoardMixin) {
    /**
     * The WBO client that will handle annotation documents local storage, server synchronization and message exchange.
     */
    private wboClient: WboClient;

    private pendingBoardInitData: { height: number; width: number; isTransparency: boolean } | null = null;

    private async initVariables(): Promise<any> {
        
        const wboPersistenceProvider = WboPersistenceLocatorProvider.getInstance(this.currentBoard);
                     
        this.drawer = new EdWboDrawer({
            isHostUser: this.isHostUser,
            username: this.userId,
            userFullName: this.userFullName,
            scale: 1.0,
            drawingEvent: true,
            isIE: /MSIE|Trident/.test(window.navigator.userAgent),
            svg: this.svgElement,
            drawingArea: this.drawingAreaElement,
            board: this.boardElement,
            pageContainer: this.pageContainerElement,
            cursors: this.cursorsElement,
            size: toolRanges().chooseSize.value,
            opacity: toolRanges().chooseOpacity.value,
            boardParameters: this.boardParameters,
            annotations: [],
            showMarker: !this.isPresentationStudentMode,
            showMyCursor: !this.isPresentationStudentMode,
            showOtherCursors: true,
            othersCursorMode: this.isTransparency ? OthersCursorMode.HostOnly : OthersCursorMode.All,
            othersCursorStyle: this.noBackground ? CursorStyle.Full : this.isTransparency ? CursorStyle.BorderedCircle : CursorStyle.SolidCircle,
            alwaysShowCursors: WBO_CURSOR_TYPES.includes(this.boardPage.sectionType),
            messageHooks: this.noBackground ? [msg => this.resizeBlankCanvas(msg)] : [],
            extraTools: this.noBackground ? [tools => new EdWboDrawerZoom(tools)] : [],
            persistentBoard: wboPersistenceProvider,
            currentBoard: this.currentBoard,
            undoRedoStackFunctions: {
                push: this.addBoardActionToStack,
                peek: this.getLastBoardActionFromStack,
                pop: this.removeLastBoardActionFromStack,
                clean: this.cleanBoardActionStack,
                getAll: this.getAllBoardActionsFromStack
            }
        }, this.setCursorBoardMutation.bind(this));
        this.drawer.beforeDraw(this.doBeforeToolDraw);
        this.drawer.updateBoardWorkInProgress(this.updateBoardWorkInProgress);
        this.drawer.broadcastMessage(this.broadcastMessage);
        this.drawer.canSendMessage(this.canSendMessage);

        this.createEvents();
        if (!this.boardPage.addingPage) {
            this.$edEventBus.$emit(BOARD_EVENTS.INIT_BOARD_TASK, Number(this.currentBoardPage));
        }
        if (!this.socket) {
            this.drawer.unsetCurrentTool();

        }
        if (!this.isHistory) {
            const rawClientInfo = `${this.userId}_${AuthProvider.getTokenInfo().jti}@${BrowserService.getFullBrowserInfo()}`;
            this.wboClient = new WboClient({
                annotationFormatVersion: this.annotationVersion,
                boardName: this.currentBoard,
                clientId: simpleHash(rawClientInfo),
                drawingAdapter: new WboDefaultDrawingAdapter(this.drawer, this.currentBoard),
                wboMaxEmitCount: this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_EMIT_COUNT],
                wboMaxEmitCountPeriod: this.boardParameters[BOARD_CONFIG_PARAMETERS.MAX_EMIT_COUNT_PERIOD],
                persistenceProvider: wboPersistenceProvider,
                transportProvider: new WboMixedTransportProvider(
                    new WboSocketIoTransportProvider(this.socket, this.currentBoard, {
                        ackTimeout: NETWORK_SETTINGS.socketIo.ackTimeout,
                        emitRetries: 3
                    }),
                    this.currentBoard),
                userContext: {
                    username: this.userId,
                    isOwner: this.isOwner,
                    isHost: this.isHostUser
                },
                onConflict: (localDocument: WboDocument, conflictingChange: WboVersionedAnnotationData, instance: WboClient) => {
                    this.onAnnotationsConflict(localDocument, conflictingChange, instance);
                },
                onLoad: (wboDocument: WboDocument, pendingChanges: WboPendingChangesQueue) => {
                    this.onAnnotationsLoaded(wboDocument);
                }
            });
            if (this.pendingBoardInitData) {
                await this.wboClient.initBoard(this.pendingBoardInitData);
                this.pendingBoardInitData = null;
            }
            this.emitWboClientCreated();
        }
    }

    @Emit('wbo-client-created')
    private emitWboClientCreated() {
        return this.wboClient;
    }

    private createBoardPage(pageData: { isTransparency?: boolean }): void {
        this.boardDimensions.height = this.boardPage.height;
        this.boardDimensions.width = this.boardPage.width;

        const boardInitData = {
            height: this.boardDimensions.height,
            width: this.boardDimensions.width,
            isTransparency: !!pageData.isTransparency
        };

        if (this.wboClient) {
            this.wboClient.initBoard(boardInitData);
        } else {
            this.pendingBoardInitData = boardInitData;
        }

        if (this.boardPage.addedPage) {
            if (!this.boardPage.backgroundImageUrl) {
                this.fixPageForDoc(pageData);
                this.loadedImage = true;
                this.emitLoadedBoard();
                return;
            }
        }
        if (this.noBackground) {
            this.fixPageForDoc(pageData);
        } else {
            this.fixPageForDoc(pageData);
            this.loadedImage = true;

        }
        if (this.isHistory) {
            this.loadAnnotationsFromHistory();
        }
    }



    private async loadAnnotationsFromHistory() {
        if (!this.history?.annotations?.metadata) {
            return;
        }
        await this.clearDrawings();
        await this.handleMessage(this.history.annotations);
        this.onAnnotationsLoaded(this.history.annotations as WboDocument);
    }

    private async clearDrawings(): Promise<void> {
        this.drawer.clearDrawings();
    }

    private updateBoardAnnotations() {
        this.wboClient?.syncBoard();
    }

    /**
     * Method that gets called when the WboClient finish loading the document, whether it's from the server or from the
     * local storage.
     */
    onAnnotationsLoaded(wboDocument?: WboDocument) {
        this.loadedAnnotations = true;
        this.$store.commit(_MEETINGS_STORE.MUTATIONS.UPDATE_LOADED_BOARDS_IN_DOM, this.loadedBoardsInDom + 1);
        this.emitLoadedBoard();

        const boardAnnotations: BoardAnnotations = this.isHistory ? this.history.annotations : wboDocument.getData();
        if (RESIZE_TYPES.includes(this.boardPage.sectionType) && boardAnnotations.metadata.width !== undefined && boardAnnotations.metadata.height !== undefined) {
            this.svgElement.width.baseVal.value = boardAnnotations.metadata.width;
            this.svgElement.height.baseVal.value = boardAnnotations.metadata.height;
        }
        this.metadata = boardAnnotations.metadata;
        this.fixPageForImage();
    }

    updateBoardWorkInProgress(workInProgress: boolean) {
        this.$edEventBus.$emit(BOARD_EVENTS.BOARD_WORK_IN_PROGRESS, this.currentBoard, workInProgress);
    }

    broadcastMessage(message: any, onDoneCallback: Function) {
        onDoneCallback?.();
    }

    /**
     * Triggered by the WboClient when a conflict is detected between the local and remote documents.
     */
    onAnnotationsConflict(localDocument: WboDocument, conflictingChange: WboVersionedAnnotationData, instance: WboClient) {
        this.$emit('annotation-conflict', localDocument, conflictingChange, instance);
    }

    mounted() {
        this.$edEventBus.$on(BOARD_EVENTS.ENABLE_BOARD_TOOLS, this.enableDefaultTool);
        this.$edEventBus.$on(`${BOARD_EVENTS.CHANGE_TOOGLE_TOOL}-${this.boardName}`, this.changeTool);
        this.$edEventBus.$on(`${BOARD_EVENTS.CHANGE_BOARD_COLOR}-${this.boardName}`, this.changeColor);
        this.$edEventBus.$on(`${BOARD_EVENTS.CHANGE_TOOL_SIZE}-${this.boardName}`, this.changeToolSize);
        this.$edEventBus.$on(`${BOARD_EVENTS.CHANGE_TOOL_OPACITY}-${this.boardName}`, this.changeToolOpacity);
        this.$edEventBus.$on(`${BOARD_EVENTS.LOADED_PDF_PAGE}${this.currentBoardPage}`, this.createBoardPage);
        this.$edEventBus.$on(`${BOARD_EVENTS.CREATE_BOARD_PAGE}${this.boardPage.page}`, this.createBoardPage);
        this.$edEventBus.$on(`${BOARD_EVENTS.BOARD_ANNOTATIONS_UPDATED}`, this.updateBoardAnnotations);
        this.$edEventBus.$on(`${BOARD_EVENTS.HISTORY_READY}`, this.loadAnnotationsFromHistory);
        this.$edEventBus.$on(BOARD_EVENTS.RESIZE_BOARD, this.resizeBoard);
        this.$edEventBus.$on(`${BOARD_EVENTS.RESIZE_BOARD}-${this.boardPage.page}`, this.resizeBoard);
        this.$edEventBus.$on(`${HOMEWORKS_EVENTS.CHANGE_SPLIT_TAB}`, this.resizeBoard);


        this.initVariables();
        this.setCurrentMenuOptions();
    }

    beforeDestroy() {
        this.$edEventBus.$off(BOARD_EVENTS.ENABLE_BOARD_TOOLS, this.enableDefaultTool);
        this.$edEventBus.$off(`${BOARD_EVENTS.CHANGE_TOOGLE_TOOL}-${this.boardName}`, this.changeTool);
        this.$edEventBus.$off(`${BOARD_EVENTS.CHANGE_BOARD_COLOR}-${this.boardName}`, this.changeColor);
        this.$edEventBus.$off(`${BOARD_EVENTS.CHANGE_TOOL_SIZE}-${this.boardName}`, this.changeToolSize);
        this.$edEventBus.$off(`${BOARD_EVENTS.CHANGE_TOOL_OPACITY}-${this.boardName}`, this.changeToolOpacity);
        this.$edEventBus.$off(this.currentBoard, this.handleMessage);
        this.$edEventBus.$off(`${BOARD_EVENTS.LOADED_PDF_PAGE}${this.boardPage.page}`, this.createBoardPage);
        this.$edEventBus.$off(`${BOARD_EVENTS.CREATE_BOARD_PAGE}${this.boardPage.page}`, this.createBoardPage);
        this.$edEventBus.$off(`${BOARD_EVENTS.BOARD_ANNOTATIONS_UPDATED}`, this.updateBoardAnnotations);
        this.$edEventBus.$off(`${BOARD_EVENTS.HISTORY_READY}`, this.loadAnnotationsFromHistory);
        this.$edEventBus.$off(BOARD_EVENTS.RESIZE_BOARD, this.resizeBoard);
        this.$edEventBus.$off(`${BOARD_EVENTS.RESIZE_BOARD}-${this.boardPage.page}`, this.resizeBoard);
        this.$edEventBus.$off(`${HOMEWORKS_EVENTS.CHANGE_SPLIT_TAB}`, this.resizeBoard);

        this.drawer.dispose();
        this.drawer = null;
        this.wboClient?.close();
        this.removeSvgClickEventListener();
    }

}

export default Vue.component('ed-board-v2', EdBoardV2);
